import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * The opt-in wizard flow for products tagged `fit-diff-variants`.
 *
 *   Step 1 — Color is pre-selected; only fits with at least one in-stock allowed-size variant are shown. ATC disabled.
 *   Step 2 — Picking a fit reveals only sizes within that fit's allowed set that are also in stock for Color+Fit. ATC stays disabled.
 *   Step 3 — Picking a size unlocks ATC.
 *
 * The element renders a sibling controller next to `<variant-picker>` and emits no UI of its own — it
 * reads the embedded variants matrix + fit→sizes map, listens to picker `change` events for instant UX,
 * and re-reconciles after `variant:update` (post-fetch morph) to win against `product-form-component`
 * and `sticky-add-to-cart`'s auto-enable handlers.
 *
 * @typedef {Object} VariantData
 * @property {number} id
 * @property {string[]} options - Positional values, e.g. ["Futura Blue", "Youth", "M"].
 * @property {boolean} available
 *
 * @typedef {{ color: string | null, fit: string | null, size: string | null }} WizardState
 *
 * @typedef {{ index: number, fieldset: HTMLElement } | null} OptionLocator
 */
class VariantFilterComponent extends Component {
  /** @type {VariantData[]} */
  #variants = [];

  /** @type {Record<string, string[]>} */
  #fitSizeMap = {};

  /** @type {WizardState} */
  #state = { color: null, fit: null, size: null };

  /** @type {{ color: OptionLocator, fit: OptionLocator, size: OptionLocator }} */
  #options = { color: null, fit: null, size: null };

  /** @type {AbortController | null} */
  #abortController = null;

  /** Guard against re-entrant DOM writes triggering our own change listener. */
  #applying = false;

  /** Whether the filter is wired to a picker and ready to manage state. */
  #active = false;

  connectedCallback() {
    super.connectedCallback();

    if (!this.#bootstrap()) return;

    this.#active = true;
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const scope = this.#section ?? document;

    // `change` is registered on the CAPTURE phase so we win over variant-picker.js's
    // bubble-phase handler. The picker's handler reads `selectedOptionsValues` from
    // currently-checked radios to build its request URL — if we don't reset the
    // stale Size (or stale Fit on a color change) before that read, Shopify resolves
    // the partial-match to a different-color variant and the gallery flips.
    scope.addEventListener('change', this.#onChange, { signal, capture: true });
    scope.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.#applyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#active = false;
  }

  // ─── Initialization ────────────────────────────────────────────────────────

  /**
   * Load JSON payloads and resolve which option index (in variant.options) belongs to each role.
   * @returns {boolean} `false` if data is incomplete or the picker isn't ready — caller should bail.
   */
  #bootstrap() {
    if (!this.#loadData()) return false;
    if (!this.#resolveOptionLocators()) return false;

    this.#state = {
      color: this.#readSelectedValue('color'),
      fit: null,
      size: null,
    };
    return true;
  }

  /** @returns {boolean} */
  #loadData() {
    try {
      const variantsScript = this.querySelector('script[data-variants-matrix]');
      const fitSizeScript = this.querySelector('script[data-fit-size-map]');
      if (!variantsScript?.textContent || !fitSizeScript?.textContent) {
        console.warn('[variant-filter] Missing JSON payload; flow disabled.');
        return false;
      }

      this.#variants = JSON.parse(variantsScript.textContent);
      this.#fitSizeMap = JSON.parse(fitSizeScript.textContent);
      return Array.isArray(this.#variants) && this.#variants.length > 0;
    } catch (error) {
      console.error('[variant-filter] Failed to parse JSON payload:', error);
      return false;
    }
  }

  /**
   * Find the fieldset for each role and the matching index into variant.options.
   * @returns {boolean} `false` if any of color/fit/size is missing — flow can't run.
   */
  #resolveOptionLocators() {
    const picker = this.#picker;
    if (!picker) return false;

    /** @type {Array<'color' | 'fit' | 'size'>} */
    const roles = ['color', 'fit', 'size'];
    for (const role of roles) {
      const fieldset = picker.querySelector(`[data-option-role="${role}"]`);
      if (!(fieldset instanceof HTMLElement)) {
        console.warn(`[variant-filter] Missing fieldset for role "${role}"; flow disabled.`);
        return false;
      }

      const rawIndex = fieldset.getAttribute('data-fieldset-index');
      const index = rawIndex !== null ? Number.parseInt(rawIndex, 10) : NaN;
      if (Number.isNaN(index)) {
        console.warn(`[variant-filter] Fieldset for role "${role}" missing data-fieldset-index.`);
        return false;
      }

      this.#options[role] = { index, fieldset };
    }
    return true;
  }

  // ─── DOM accessors ─────────────────────────────────────────────────────────

  /** @returns {HTMLElement | null} */
  get #section() {
    const section = this.closest('.shopify-section');
    return section instanceof HTMLElement ? section : null;
  }

  /** @returns {HTMLElement | null} */
  get #picker() {
    const sectionId = this.dataset.sectionId;
    const productId = this.dataset.productId;
    if (!sectionId || !productId) return null;

    const selector = `variant-picker[data-section-id="${sectionId}"][data-product-id="${productId}"]`;
    const picker = (this.#section ?? document).querySelector(selector);
    return picker instanceof HTMLElement ? picker : null;
  }

  /**
   * @param {'color' | 'fit' | 'size'} role
   * @returns {HTMLInputElement[]}
   */
  #radiosFor(role) {
    const locator = this.#options[role];
    if (!locator) return [];
    return Array.from(locator.fieldset.querySelectorAll('input[type="radio"]'));
  }

  /**
   * @param {'color' | 'fit' | 'size'} role
   * @returns {string | null}
   */
  #readSelectedValue(role) {
    const checked = this.#radiosFor(role).find((r) => r.checked);
    return checked?.value ?? null;
  }

  // ─── Event handlers ────────────────────────────────────────────────────────

  /** @param {Event} event */
  #onChange = (event) => {
    if (!this.#active || this.#applying) return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    const role = this.#roleForElement(target);
    if (!role) return;

    switch (role) {
      case 'color':
        this.#state.color = target.value;
        this.#state.fit = null;
        this.#state.size = null;
        break;
      case 'fit':
        this.#state.fit = target.value;
        this.#state.size = null;
        break;
      case 'size':
        this.#state.size = target.value;
        break;
    }

    this.#applyState();
  };

  /**
   * Re-reconcile after the picker has been morphed with a freshly-fetched section.
   * Deferred so we sequence after `product-form-component` / `sticky-add-to-cart` enable handlers.
   */
  #onVariantUpdate = () => {
    if (!this.#active) return;
    queueMicrotask(() => this.#applyState());
  };

  /**
   * @param {HTMLInputElement} input
   * @returns {'color' | 'fit' | 'size' | null}
   */
  #roleForElement(input) {
    const picker = this.#picker;
    if (!picker || !picker.contains(input)) return null;

    const fieldset = input.closest('[data-option-role]');
    if (!(fieldset instanceof HTMLElement)) return null;
    const role = fieldset.getAttribute('data-option-role');
    return role === 'color' || role === 'fit' || role === 'size' ? role : null;
  }

  // ─── Reconciliation ────────────────────────────────────────────────────────

  #applyState() {
    if (!this.#active) return;
    this.#applying = true;
    try {
      this.#applyFitVisibility();
      this.#applySizeVisibility();
      this.#syncCheckedState();
      this.#applyBuyButtons();
    } finally {
      this.#applying = false;
    }
  }

  /** Hide fits without any in-stock allowed-size variant for the current color. */
  #applyFitVisibility() {
    const fitLocator = this.#options.fit;
    const color = this.#state.color;
    if (!fitLocator || !color) return;

    for (const input of this.#radiosFor('fit')) {
      const visible = this.#hasInStockSizeFor(color, input.value);
      this.#setLabelVisibility(input, visible);
    }
  }

  /**
   * Hide the entire size fieldset when no fit is selected; otherwise show
   * every size in the fit's allowed set. Sizes outside the allowed set are
   * fully hidden; out-of-stock sizes inside the allowed set stay visible
   * (with the theme's strikethrough treatment from `data-option-available='false'`)
   * but are made non-interactive via `disabled` so clicks/keyboard can't select them.
   */
  #applySizeVisibility() {
    const sizeLocator = this.#options.size;
    if (!sizeLocator) return;

    const { fit, color } = this.#state;

    if (!fit || !color) {
      this.#setElementVisibility(sizeLocator.fieldset, false);
      return;
    }
    this.#setElementVisibility(sizeLocator.fieldset, true);

    const allowed = this.#fitSizeMap[fit] ?? [];
    for (const input of this.#radiosFor('size')) {
      const inAllowed = allowed.includes(input.value);
      const inStock = input.dataset.optionAvailable !== 'false';
      this.#setLabelVisibility(input, inAllowed);
      input.disabled = !inAllowed || !inStock;
    }
  }

  /** Reconcile radio.checked to mirror our state (Shopify auto-selects on every fetch). */
  #syncCheckedState() {
    /** @type {Array<'fit' | 'size'>} */
    const roles = ['fit', 'size'];
    for (const role of roles) {
      const expected = this.#state[role];
      for (const radio of this.#radiosFor(role)) {
        const shouldBeChecked = expected !== null && radio.value === expected;
        if (radio.checked !== shouldBeChecked) {
          radio.checked = shouldBeChecked;
        }
        // morph.js preserves data-current-checked/data-previous-checked across fetches —
        // wipe them when deselecting so the pill-animation tracking doesn't leak in
        // visually (cached pill-width var, "previous" marker on a stale radio, etc).
        if (!shouldBeChecked) {
          radio.dataset.currentChecked = 'false';
          radio.dataset.previousChecked = 'false';
        }
      }
    }
  }

  /**
   * Disable all buy buttons in the section until the wizard reaches Step 3.
   *
   * While the wizard is in Step 1 or 2, the picker fetches partial-match
   * variants on every change, and Shopify morphs the button HTML with
   * availability text (e.g. "Sold out", "Unavailable"). That's noise the
   * customer can't act on yet — pin the label to the localized "Add to cart"
   * string until Step 3 lets real availability shine through.
   */
  #applyBuyButtons() {
    const section = this.#section ?? document;
    const ready = this.#state.size !== null;
    const fallbackText = this.dataset.addToCartText ?? '';

    /** @type {NodeListOf<HTMLButtonElement>} */
    const buttons = section.querySelectorAll(
      'add-to-cart-component button[ref="addToCartButton"], sticky-add-to-cart button[ref="addToCartButton"]'
    );
    for (const button of buttons) {
      button.disabled = !ready;
      if (!ready && fallbackText) {
        this.#setButtonText(button, fallbackText);
      }
    }

    /** @type {NodeListOf<HTMLElement>} */
    const acceleratedCheckouts = section.querySelectorAll('[ref="acceleratedCheckoutButtonContainer"]');
    for (const container of acceleratedCheckouts) {
      container.hidden = !ready;
    }
  }

  /**
   * Replace the visible label on an ATC button without touching the surrounding
   * span scaffolding (preserves the burst-animation siblings).
   *
   * @param {HTMLButtonElement} button
   * @param {string} text
   */
  #setButtonText(button, text) {
    const innermost = button.querySelector('.add-to-cart-text__content span span');
    if (innermost instanceof HTMLElement && innermost.textContent !== text) {
      innermost.textContent = text;
    }
  }

  // ─── Inventory helpers ─────────────────────────────────────────────────────

  /**
   * Does at least one variant matching (color, fit) and an allowed size exist and is available?
   * Used to decide whether a Fit should appear at all for the current Color.
   * @param {string} color
   * @param {string} fit
   */
  #hasInStockSizeFor(color, fit) {
    const allowed = this.#fitSizeMap[fit] ?? [];
    if (allowed.length === 0) return false;

    const ci = this.#options.color?.index ?? 0;
    const fi = this.#options.fit?.index ?? 1;
    const si = this.#options.size?.index ?? 2;

    return this.#variants.some(
      (v) => v.available && v.options[ci] === color && v.options[fi] === fit && allowed.includes(v.options[si])
    );
  }

  // ─── Visibility primitive ──────────────────────────────────────────────────

  /**
   * Toggle visibility via inline style — the theme sets `display: flex` on
   * `.variant-option--buttons` and `.variant-option__button-label`, which
   * outranks the UA `[hidden] { display: none }` due to class specificity.
   * Inline style always wins.
   *
   * @param {HTMLElement} element
   * @param {boolean} visible
   */
  #setElementVisibility(element, visible) {
    if (visible) {
      element.style.removeProperty('display');
    } else {
      element.style.display = 'none';
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {boolean} visible
   */
  #setLabelVisibility(input, visible) {
    const label = input.closest('label');
    if (label instanceof HTMLElement) {
      this.#setElementVisibility(label, visible);
    }
  }
}

if (!customElements.get('variant-filter')) {
  customElements.define('variant-filter', VariantFilterComponent);
}
